/**
 * CESM OSINT System - Data Coherence Alert Banner
 * Affiche les alertes de cohérence et auto-réparation des données
 * 
 * Concept & Développement: CDT (R) Jean-Baptiste GIRAUD
 * LPP Logiciel © 2026
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Wrench, CheckCircle, X, RefreshCw } from 'lucide-react';

const CoherenceAlertBanner = ({ repairs, onDismiss, onRetry }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  
  if (!repairs || repairs.length === 0 || !isVisible) {
    return null;
  }

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss && onDismiss();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="mb-4"
      >
        <div className="bg-amber-900/30 border-2 border-amber-500/50 rounded-lg overflow-hidden">
          {/* Header */}
          <div 
            className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-amber-900/50 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                <motion.div
                  className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full"
                  animate={{ scale: [1, 1.3, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              </div>
              <div>
                <p className="font-mono text-amber-300 text-sm font-bold uppercase tracking-wider">
                  PROTOCOLE AUTO-RÉPARATION ACTIVÉ
                </p>
                <p className="font-mono text-amber-200/70 text-xs">
                  {repairs.length} correction{repairs.length > 1 ? 's' : ''} appliquée{repairs.length > 1 ? 's' : ''} automatiquement
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {onRetry && (
                <button
                  onClick={(e) => { e.stopPropagation(); onRetry(); }}
                  className="p-2 hover:bg-amber-500/20 rounded transition-colors"
                  title="Relancer la récupération"
                >
                  <RefreshCw className="w-4 h-4 text-amber-400" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
                className="p-2 hover:bg-red-500/20 rounded transition-colors"
                title="Fermer"
              >
                <X className="w-4 h-4 text-amber-400" />
              </button>
            </div>
          </div>
          
          {/* Details - Expandable */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-amber-500/30 overflow-hidden"
              >
                <div className="px-4 py-3 bg-black/30">
                  <p className="font-mono text-amber-300/80 text-xs mb-3 uppercase tracking-wider">
                    VERBOSE DES OPÉRATIONS:
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {repairs.map((repair, idx) => (
                      <div 
                        key={idx}
                        className="flex items-start gap-2 font-mono text-xs bg-black/30 p-2 rounded border border-amber-500/20"
                      >
                        <Wrench className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-amber-400 font-bold">{repair.field}</span>
                            <span className="text-red-400 line-through">{repair.original_value ?? 'null'}</span>
                            <span className="text-white">→</span>
                            <span className="text-green-400">{repair.repaired_value}</span>
                          </div>
                          <p className="text-white/80 text-xs">
                            Raison: {repair.reason}
                          </p>
                        </div>
                        <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                  <p className="font-mono text-white/40 text-xs mt-3 italic">
                    Les données ont été corrigées automatiquement. Un email de notification a été envoyé aux administrateurs.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CoherenceAlertBanner;
